import { useContext, useEffect, useState } from "react";
import {
  createGradableModel,
  deleteModel,
  getGradableModels,
  getSerialsInfos,
  updateGradableModel,
} from "../../../components/gradableModels/functions";
import { formatarData } from "../../../components/utils/functions";
import ToastContext from "../../../context/toastProvider";
import MobileFilterSheet from "../../ui/MobileFilterSheet";
import MobileFormField from "../../ui/MobileFormField";
import MobileConfirm from "../../ui/MobileConfirm";
import MobileSkeleton from "../../ui/MobileSkeleton";

const inputClass =
  "w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha disabled:bg-monao";
const primaryBtn =
  "inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50";
const secondaryBtn =
  "inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg border border-primary/50 px-4 text-sm font-medium text-laranjinha/80 hover:bg-monao disabled:opacity-50";
const dangerBtn =
  "inline-flex min-h-[44px] items-center justify-center rounded-lg bg-red-700 px-4 text-sm font-medium text-white hover:bg-red-800";

function buildCreateBody(fields) {
  const body = {};
  if (fields.game) body.game = fields.game;
  if (fields.name) body.name = fields.name;
  if (fields.edition) body.edition = fields.edition;
  if (fields.year) body.year = fields.year;
  if (fields.language) body.language = fields.language;
  if (fields.numbering) body.numbering = fields.numbering;
  if (fields.rarity) body.rarity = fields.rarity;
  if (fields.initial) body.serialInitial = fields.initial.toUpperCase();
  return body;
}

function buildUpdateBody(fields, original) {
  const changes = {};
  if (fields.game !== original.game) changes.game = fields.game;
  if (fields.name !== original.name) changes.name = fields.name;
  if (fields.edition !== original.edition) changes.edition = fields.edition;
  if (fields.year !== original.year) changes.year = fields.year;
  if (fields.language !== original.language) changes.language = fields.language;
  if (fields.numbering !== original.numbering) changes.numbering = fields.numbering;
  if (fields.rarity !== original.rarity) changes.rarity = fields.rarity;
  if (fields.serialInitial !== original.serialInitial) {
    changes.serialInitial = fields.serialInitial;
  }
  return changes;
}

export function MobileCreateGradableModelSheet({ open, onClose, user, onCreated }) {
  const { goodToast, badToast } = useContext(ToastContext);

  const [game, setGame] = useState("");
  const [name, setName] = useState("");
  const [edition, setEdition] = useState("");
  const [year, setYear] = useState("");
  const [language, setLanguage] = useState("");
  const [numbering, setNumbering] = useState("");
  const [rarity, setRarity] = useState("");
  const [initial, setInitial] = useState("");
  const [copyMode, setCopyMode] = useState(false);
  const [copyId, setCopyId] = useState("");
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setGame("");
    setName("");
    setEdition("");
    setYear("");
    setLanguage("");
    setNumbering("");
    setRarity("");
    setInitial("");
    setCopyMode(false);
    setCopyId("");
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    setLoading(true);
    const data = await createGradableModel(
      user.token,
      buildCreateBody({ game, name, edition, year, language, numbering, rarity, initial })
    );
    setLoading(false);

    if (data.type === "success") {
      goodToast("Modelo avaliável cadastrado");
      resetForm();
      onCreated();
    }
    if (data.type === "error") {
      badToast(data.message);
    }
  };

  const handleCopySearch = async () => {
    setLoading(true);
    const response = await getGradableModels(user.token, copyId, null, null, null);
    setLoading(false);

    if (response.type === "success" && response.data?.[0]) {
      const found = response.data[0];
      setGame(found.game || "");
      setName(found.name || "");
      setEdition(found.edition || "");
      setYear(found.year || "");
      setLanguage(found.language || "");
      setNumbering(found.numbering || "");
      setRarity(found.rarity || "");
      setInitial(found.serialInitial || "");
      setCopyMode(false);
    } else if (response.type === "error") {
      badToast(response.message);
    } else {
      badToast("Modelo não encontrado");
    }
  };

  if (!open) return null;

  return (
    <MobileFilterSheet open={open} onClose={handleClose} title="Novo modelo avaliável">
      <div className="space-y-4">
        <MobileFormField label="Jogo">
          <input
            type="text"
            aria-label="Jogo"
            value={game}
            onChange={(e) => setGame(e.target.value)}
            className={inputClass}
          />
        </MobileFormField>
        <MobileFormField label="Nome">
          <input
            type="text"
            aria-label="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClass}
          />
        </MobileFormField>
        <MobileFormField label="Edição">
          <input
            type="text"
            aria-label="Edição"
            value={edition}
            onChange={(e) => setEdition(e.target.value)}
            className={inputClass}
          />
        </MobileFormField>
        <MobileFormField label="Ano">
          <input
            type="text"
            aria-label="Ano"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className={inputClass}
          />
        </MobileFormField>
        <MobileFormField label="Idioma">
          <input
            type="text"
            aria-label="Idioma"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className={inputClass}
          />
        </MobileFormField>
        <MobileFormField label="Numeração">
          <input
            type="text"
            aria-label="Numeração"
            value={numbering}
            onChange={(e) => setNumbering(e.target.value)}
            className={inputClass}
          />
        </MobileFormField>
        <MobileFormField label="Raridade">
          <input
            type="text"
            aria-label="Raridade"
            value={rarity}
            onChange={(e) => setRarity(e.target.value)}
            className={inputClass}
          />
        </MobileFormField>
        <MobileFormField label="Inicial serial">
          <input
            type="text"
            aria-label="Inicial serial"
            value={initial}
            onChange={(e) => setInitial(e.target.value)}
            className={inputClass}
          />
        </MobileFormField>

        {copyMode ? (
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              value={copyId}
              onChange={(e) => setCopyId(e.target.value)}
              placeholder="ID do modelo"
              className={inputClass}
            />
            <button type="button" onClick={handleCopySearch} className={primaryBtn}>
              Buscar
            </button>
            <button
              type="button"
              onClick={() => setCopyMode(false)}
              className={secondaryBtn}
            >
              Cancelar
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setCopyMode(true)}
            className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg border border-purple-300 bg-primary/10 px-4 text-sm font-medium text-purple-800 hover:bg-purple-100"
          >
            Copiar modelo
          </button>
        )}

        <div className="flex gap-3">
          <button type="button" onClick={handleClose} className={secondaryBtn}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className={primaryBtn}
          >
            Cadastrar
          </button>
        </div>
      </div>
    </MobileFilterSheet>
  );
}

function ModelFields({
  fields,
  setFields,
  disabled,
}) {
  return (
    <>
      <MobileFormField label="Jogo">
        <input
          type="text"
          aria-label="Jogo"
          value={fields.game}
          onChange={(e) => setFields((current) => ({ ...current, game: e.target.value }))}
          disabled={disabled}
          className={inputClass}
        />
      </MobileFormField>
      <MobileFormField label="Nome">
        <input
          type="text"
          aria-label="Nome"
          value={fields.name}
          onChange={(e) => setFields((current) => ({ ...current, name: e.target.value }))}
          disabled={disabled}
          className={inputClass}
        />
      </MobileFormField>
      <MobileFormField label="Edição">
        <input
          type="text"
          aria-label="Edição"
          value={fields.edition}
          onChange={(e) => setFields((current) => ({ ...current, edition: e.target.value }))}
          disabled={disabled}
          className={inputClass}
        />
      </MobileFormField>
      <MobileFormField label="Ano">
        <input
          type="text"
          aria-label="Ano"
          value={fields.year}
          onChange={(e) => setFields((current) => ({ ...current, year: e.target.value }))}
          disabled={disabled}
          className={inputClass}
        />
      </MobileFormField>
      <MobileFormField label="Idioma">
        <input
          type="text"
          aria-label="Idioma"
          value={fields.language}
          onChange={(e) => setFields((current) => ({ ...current, language: e.target.value }))}
          disabled={disabled}
          className={inputClass}
        />
      </MobileFormField>
      <MobileFormField label="Numeração">
        <input
          type="text"
          aria-label="Numeração"
          value={fields.numbering}
          onChange={(e) => setFields((current) => ({ ...current, numbering: e.target.value }))}
          disabled={disabled}
          className={inputClass}
        />
      </MobileFormField>
      <MobileFormField label="Raridade">
        <input
          type="text"
          aria-label="Raridade"
          value={fields.rarity}
          onChange={(e) => setFields((current) => ({ ...current, rarity: e.target.value }))}
          disabled={disabled}
          className={inputClass}
        />
      </MobileFormField>
      <MobileFormField label="Inicial serial">
        <input
          type="text"
          aria-label="Inicial serial"
          value={fields.serialInitial}
          onChange={(e) =>
            setFields((current) => ({ ...current, serialInitial: e.target.value }))
          }
          disabled={disabled}
          className={inputClass}
        />
      </MobileFormField>
    </>
  );
}

export function MobileGradableModelDetailSheet({
  open,
  onClose,
  model,
  user,
  onChanged,
}) {
  const { goodToast, badToast } = useContext(ToastContext);

  const [tab, setTab] = useState(0);
  const [enableChanges, setEnableChanges] = useState(false);
  const [fields, setFields] = useState({
    game: "",
    name: "",
    edition: "",
    year: "",
    language: "",
    numbering: "",
    rarity: "",
    serialInitial: "",
  });
  const [serialsLoading, setSerialsLoading] = useState(false);
  const [serialsData, setSerialsData] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!model) return;
    setTab(0);
    setEnableChanges(false);
    setFields({
      game: model.game ?? "",
      name: model.name ?? "",
      edition: model.edition ?? "",
      year: model.year ?? "",
      language: model.language ?? "",
      numbering: model.numbering ?? "",
      rarity: model.rarity ?? "",
      serialInitial: model.serialInitial ?? "",
    });
    setSerialsData(null);
    setDeleteConfirmOpen(false);
  }, [model]);

  useEffect(() => {
    if (!open || !model || tab !== 1) return;

    let active = true;
    const fetchSerials = async () => {
      setSerialsLoading(true);
      const data = await getSerialsInfos(user.token, model._id);
      if (!active) return;
      if (data.type === "error") {
        badToast(data.message);
      }
      setSerialsData(data);
      setSerialsLoading(false);
    };

    fetchSerials();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, model, tab, user?.token]);

  const handleClose = () => {
    setEnableChanges(false);
    setTab(0);
    onClose();
  };

  const handleUpdate = async () => {
    const changes = buildUpdateBody(fields, model);
    if (!Object.keys(changes).length) {
      badToast("Nenhuma alteração para salvar");
      return;
    }

    setLoading(true);
    const data = await updateGradableModel(user.token, model._id, changes);
    setLoading(false);

    if (data.type === "success") {
      goodToast("Modelo avaliável atualizado");
      onChanged();
    }
    if (data.type === "error") {
      badToast(data.message);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    const data = await deleteModel(user.token, model._id);
    setLoading(false);
    setDeleteConfirmOpen(false);

    if (data.type === "success") {
      goodToast("Modelo excluído!");
      onChanged();
    }
    if (data.type === "error") {
      badToast(data.message);
    }
  };

  if (!open || !model) return null;

  const tabClass = (index) =>
    index === tab
      ? "rounded-full bg-primary px-3 py-1 text-sm font-medium text-mgs-bg"
      : "rounded-full border border-primary/50 px-3 py-1 text-sm text-primary";

  return (
    <MobileFilterSheet open={open} onClose={handleClose} title="Detalhe do modelo">
      <div className="space-y-4">
        <div className="flex gap-2">
          <button type="button" className={tabClass(0)} onClick={() => setTab(0)}>
            Infos
          </button>
          <button type="button" className={tabClass(1)} onClick={() => setTab(1)}>
            Seriais
          </button>
        </div>

        {tab === 0 ? (
          <>
            <MobileFormField label="ID">
              <input
                type="text"
                value={model._id}
                disabled
                className={inputClass}
                aria-label="ID"
              />
            </MobileFormField>
            <ModelFields fields={fields} setFields={setFields} disabled={!enableChanges} />
            <button
              type="button"
              onClick={() => setEnableChanges((current) => !current)}
              className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-primary/20 px-4 text-sm font-medium text-primary hover:bg-primary/30"
            >
              {enableChanges ? "Bloquear alterações" : "Liberar alterações"}
            </button>
            <div className="flex gap-3">
              <button type="button" onClick={handleClose} className={secondaryBtn}>
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleUpdate}
                disabled={!enableChanges || loading}
                className={primaryBtn}
              >
                Atualizar
              </button>
            </div>
          </>
        ) : serialsLoading ? (
          <MobileSkeleton lines={4} />
        ) : serialsData?.type === "success" ? (
          <div className="space-y-3 rounded-lg border border-primary/30 p-3 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-laranjinha/60">Nº de seriais</span>
              <span className="font-medium text-laranjinha">{serialsData.data.count}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-laranjinha/60">Maior nota</span>
              <span className="font-medium text-laranjinha">
                {serialsData.data.max.generalScore} · {serialsData.data.max.serialNumber}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-laranjinha/60">Menor nota</span>
              <span className="font-medium text-laranjinha">
                {serialsData.data.min.generalScore} · {serialsData.data.min.serialNumber}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-laranjinha/60">Último cadastro</span>
              <span className="font-medium text-laranjinha">
                {serialsData.data.last.serialNumber} em{" "}
                {formatarData(serialsData.data.last.createdAt)}
              </span>
            </div>
            <button type="button" onClick={handleClose} className={`${dangerBtn} w-full`}>
              Fechar
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-laranjinha/70">
              Nenhum serial encontrado para esse modelo.
            </p>
            <button
              type="button"
              onClick={() => setDeleteConfirmOpen(true)}
              className={`${dangerBtn} w-full`}
            >
              Excluir modelo
            </button>
            <button type="button" onClick={handleClose} className={`${secondaryBtn} w-full`}>
              Fechar
            </button>
          </div>
        )}
      </div>

      <MobileConfirm
        open={deleteConfirmOpen}
        title="Excluir modelo"
        message="Tem certeza que deseja excluir este modelo avaliável?"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </MobileFilterSheet>
  );
}
